import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import {
  api, type Club, type Slot, formatTime, formatShortDate, formatZar, todayYmd, addDaysYmd,
} from "../lib/api";
import { Shell } from "../components/Shell";

export default function ClubDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [ymd, setYmd] = useState(todayYmd());
  const [club, setClub] = useState<Club | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [notBookable, setNotBookable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setErr(null);
    api.listSlots(slug, ymd)
      .then(r => { setClub(r.club); setSlots(r.slots ?? []); setNotBookable(!!r.not_bookable); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [slug, ymd]);

  const days = useMemo(() => {
    const t = todayYmd();
    return Array.from({ length: 7 }, (_, i) => addDaysYmd(t, i));
  }, []);

  return (
    <Shell>
      <section className="relative">
        <div className="aspect-[21/9] sm:aspect-[3/1] bg-neutral-900 overflow-hidden">
          {club?.photo_url && (
            <img src={club.photo_url} alt={club.name} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-transparent" />
        </div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-6xl px-6 pb-8">
            <Link href="/clubs" className="text-sm text-neutral-400 hover:text-neutral-0">← All clubs</Link>
            <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-0">
              {club?.name ?? "…"}
            </h1>
            <div className="mt-2 text-neutral-400">
              {club && [club.city, club.province].filter(Boolean).join(", ")}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-12 pb-20">
        {club?.description && (
          <p className="text-neutral-300 max-w-3xl leading-relaxed">{club.description}</p>
        )}

        {club?.amenities && club.amenities.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {club.amenities.map(a => (
              <span key={a} className="text-xs text-neutral-400 rounded-full border border-neutral-800 bg-neutral-900/50 px-3 py-1">
                {a}
              </span>
            ))}
          </div>
        )}

        <div className="mt-12 border-t border-neutral-900 pt-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-2xl font-semibold tracking-tight">Available tee times</h2>
            <div className="text-sm text-neutral-500">Pay at the club · {club?.currency ?? "ZAR"}</div>
          </div>

          {notBookable && (
            <div className="mt-6 rounded-xl border border-neutral-800 bg-neutral-900/50 p-6 text-neutral-400">
              Online booking is coming soon for this club.
            </div>
          )}

          {!notBookable && (
            <>
              <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
                {days.map(d => (
                  <button
                    key={d}
                    onClick={() => setYmd(d)}
                    className={
                      "shrink-0 rounded-xl px-4 py-3 text-sm transition-colors border " +
                      (d === ymd
                        ? "bg-accent-500 text-neutral-950 border-accent-500"
                        : "bg-neutral-900/50 border-neutral-800 text-neutral-300 hover:border-neutral-700")
                    }
                  >
                    <div className="font-medium">{formatShortDate(`${d}T08:00:00+02:00`)}</div>
                  </button>
                ))}
              </div>

              {err && <div className="mt-6 rounded-lg border border-red-900/50 bg-red-950/30 text-red-200 p-4 text-sm">{err}</div>}

              {loading ? (
                <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-20 rounded-xl bg-neutral-900/50 border border-neutral-900 animate-pulse" />
                  ))}
                </div>
              ) : (
                <SlotGrid slots={slots} clubSlug={slug} />
              )}
            </>
          )}
        </div>
      </section>
    </Shell>
  );
}

function SlotGrid({ slots, clubSlug }: { slots: Slot[]; clubSlug: string }) {
  const available = slots.filter(s => s.status === "available" && (s.seats_remaining ?? 0) > 0);
  if (available.length === 0) {
    return (
      <div className="mt-6 rounded-xl border border-neutral-800 bg-neutral-900/50 p-6 text-neutral-400">
        No open tee times for this day. Try another date.
      </div>
    );
  }
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {available.map(s => (
        <Link
          key={s.id}
          href={`/book/${s.id}`}
          className="group rounded-xl border border-neutral-800 bg-neutral-900/30 px-4 py-3 hover:border-accent-500 hover:bg-neutral-900 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="font-medium tabular-nums">{formatTime(s.start_time)}</div>
            <div className="text-xs text-accent-400 opacity-0 group-hover:opacity-100 transition-opacity">Book →</div>
          </div>
          <div className="mt-1 flex items-center justify-between text-xs text-neutral-500">
            <span>{s.seats_remaining ?? s.max_players} of {s.max_players} open</span>
            <span className="text-neutral-300">{formatZar(s.green_fee_cents)}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}
